import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { DeliveryNoteNotification, DeliveryStatusEnum } from '../models/delivery-note.model';
import { AuthService } from './auth.service';
import { DeliveryNoteService } from './delivery-note.service';
import { SocketService } from './socket.service';

export interface AppNotification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: DeliveryNoteNotification;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private notifications: AppNotification[] = [];
  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  private newNotification = new Subject<AppNotification>();
  private subscriptions = new Subscription();
  private currentUserId: string | null = null;

  public notifications$ = this.notificationsSubject.asObservable();
  public newNotification$ = this.newNotification.asObservable();

  constructor(
    private socketService: SocketService,
    private deliveryNoteService: DeliveryNoteService,
    private authService: AuthService
  ) {
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUserId = user?._id ?? null;
      })
    );
    this.setupSocketListeners();
  }

  private setupSocketListeners(): void {
    // Listen for delivery note created
    this.subscriptions.add(
      this.socketService.deliveryNoteCreated$.subscribe(data => {
        this.addNotification({
          type: 'success',
          title: 'Neuer Lieferzettel',
          message: `Lieferzettel ${data.noteNumber} wurde erstellt`,
          data,
        });
      })
    );

    // Listen for delivery note updated
    this.subscriptions.add(
      this.socketService.deliveryNoteUpdated$.subscribe(data => {
        this.addNotification({
          type: 'info',
          title: 'Lieferzettel aktualisiert',
          message: `Lieferzettel ${data.noteNumber} wurde aktualisiert`,
          data,
        });
      })
    );

    // Listen for delivery note deleted
    this.subscriptions.add(
      this.socketService.deliveryNoteDeleted$.subscribe(data => {
        this.addNotification({
          type: 'warning',
          title: 'Lieferzettel gelöscht',
          message: `Ein Lieferzettel wurde gelöscht`,
          data,
        });
      })
    );

    // Listen for delivery note status changed
    this.subscriptions.add(
      this.socketService.deliveryNoteStatusChanged$.subscribe(data => {
        const statusName = this.getStatusName(data.status as DeliveryStatusEnum);
        this.addNotification({
          type: 'info',
          title: 'Status geändert',
          message: `Lieferzettel ${data.noteNumber} → ${statusName}`,
          data,
        });
      })
    );
  }

  private getStatusName(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusName(status);
  }

  private addNotification(params: { type: AppNotification['type']; title: string; message: string; data?: DeliveryNoteNotification }): void {
    if (params.data?.updatedBy && params.data.updatedBy === this.currentUserId) {
      return;
    }

    const notification: AppNotification = {
      id: crypto.randomUUID(),
      type: params.type,
      title: params.title,
      message: params.message,
      timestamp: new Date(),
      read: false,
      data: params.data,
    };

    this.notifications.unshift(notification);

    // Keep only last 50 notifications
    if (this.notifications.length > 50) {
      this.notifications = this.notifications.slice(0, 50);
    }

    this.notificationsSubject.next([...this.notifications]);
    this.newNotification.next(notification);
  }

  markAsRead(id: string): void {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.read = true;
      this.notificationsSubject.next([...this.notifications]);
    }
  }

  markAllAsRead(): void {
    this.notifications.forEach(n => (n.read = true));
    this.notificationsSubject.next([...this.notifications]);
  }

  clearAll(): void {
    this.notifications = [];
    this.notificationsSubject.next([]);
  }

  getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
